/**
 * Consent state — client-side.
 *
 * Storage: first-party localStorage key `sc-consent-v1` (JSON).
 *   {
 *     analytics: "granted" | "denied",
 *     timestamp: ISO string,
 *     policyVersion: 1,
 *   }
 *
 * A missing/malformed record means "no choice yet" — analytics stays denied
 * and the banner must be shown.
 *
 * The Consent Mode v2 defaults are set inline in the HTML head (see
 * `layout.tsx`), before any GA/Vercel script has a chance to run. After a
 * user choice, we call `window.gtag('consent', 'update', …)` and dispatch a
 * `sc-consent-change` event so other client modules (Vercel Analytics,
 * A/B test) can react.
 *
 * We currently expose ONE user-facing category: analytics. Everything we
 * gate today is analytics (GA4, Vercel Analytics, Vercel Speed Insights,
 * A/B test bucketing). If we add ad tracking, session replay etc. later,
 * add a category rather than reusing this one — Consent Mode v2 already
 * distinguishes them and the user has a right to a granular choice.
 */

export const CONSENT_STORAGE_KEY = "sc-consent-v1";
export const CONSENT_CHANGE_EVENT = "sc-consent-change";
export const CONSENT_POLICY_VERSION = 1;

export type ConsentValue = "granted" | "denied";

export type ConsentRecord = {
  analytics: ConsentValue;
  timestamp: string;
  policyVersion: number;
};

export type ConsentDecision = ConsentRecord | null;

/** Read the stored consent record. Returns null when no choice has been made. */
export function readConsent(): ConsentDecision {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (
      (parsed.analytics === "granted" || parsed.analytics === "denied") &&
      typeof parsed.timestamp === "string" &&
      typeof parsed.policyVersion === "number"
    ) {
      return parsed as ConsentRecord;
    }
    return null;
  } catch {
    return null;
  }
}

/** True when analytics consent is explicitly granted. Never true without a choice. */
export function isAnalyticsGranted(): boolean {
  const rec = readConsent();
  return rec?.analytics === "granted";
}

/**
 * Persist the user's choice, update Google Consent Mode, and broadcast the
 * change so listeners (Vercel Analytics loader, A/B test) can react without
 * a page reload. Withdrawal (grant → deny) also flows through this path.
 */
export function setAnalyticsConsent(value: ConsentValue): void {
  if (typeof window === "undefined") return;
  const record: ConsentRecord = {
    analytics: value,
    timestamp: new Date().toISOString(),
    policyVersion: CONSENT_POLICY_VERSION,
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* localStorage unavailable — fall back to in-memory event only. */
  }

  // Update Google Consent Mode v2. Analytics, ad-storage and personalization
  // move together in a single `update` call. We only ever grant analytics
  // today; ad_storage / ad_user_data / ad_personalization stay denied.
  try {
    const w = window as unknown as {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
    };
    if (typeof w.gtag === "function") {
      w.gtag("consent", "update", {
        analytics_storage: value,
        // Explicitly keep advertising signals off — we do not use them.
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    } else if (Array.isArray(w.dataLayer)) {
      // gtag() function not yet defined (script still loading) — push
      // directly onto the dataLayer using the same argument shape.
      w.dataLayer.push([
        "consent",
        "update",
        {
          analytics_storage: value,
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
        },
      ]);
    }
  } catch {
    /* never break UX on analytics update */
  }

  try {
    window.dispatchEvent(
      new CustomEvent(CONSENT_CHANGE_EVENT, { detail: record }),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Clear all non-essential storage that we know we set ourselves.
 *
 * Called on reject (from a previously-granted state, or as a defensive
 * sweep on first reject). Deliberately conservative — we do not attempt
 * to remove Google/Vercel-set cookies from JS (many are HttpOnly and out
 * of reach anyway). Consent Mode v2 tells Google to stop storing new
 * cookies; the existing ones expire on their own schedule. This is the
 * IAB-recommended behaviour for a first-party consent framework without
 * a full CMP.
 */
export function clearNonEssentialStorage(): void {
  if (typeof window === "undefined") return;
  const localKeys = [
    "ab_cta_variant",
    // Historical — fix/compliance-gaps removed the returning-visitor
    // personalization but a user who visited under the previous build
    // may still have this key set. Sweep it defensively.
    "sc-visited",
    // UI-only convenience state for /configurator. Contains no PII, but
    // is still non-essential and now written only when analytics is
    // granted; on withdrawal we clear it so a subsequent visit starts
    // from defaults, matching what the user just asked for.
    "sc-configurator",
  ];
  const sessionKeys = ["ab_cta_exposed", "sa_session_id"];
  try {
    for (const k of localKeys) localStorage.removeItem(k);
    // F3 fix (2026-09-11): mapbox-gl writes SDK-internal identifiers
    // under the `mapbox.eventData.*` prefix in localStorage. The
    // primary defence is in MapboxNetworkMap (EVENTS_URL override
    // prevents the writes from happening at all), but sweep the
    // prefix here too so a user who visited under an older build
    // — or a future SDK version we have not yet patched — has any
    // leftover key cleared on withdraw. Prefix sweep, not a fixed
    // list, because the key includes a per-token hash suffix.
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith("mapbox.")) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
  try {
    for (const k of sessionKeys) sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
  // Best-effort clear of GA-set _ga* cookies on our own eTLD+1.
  try {
    const host = window.location.hostname;
    const eTld =
      host.split(".").length >= 2
        ? "." + host.split(".").slice(-2).join(".")
        : host;
    for (const c of document.cookie.split(";")) {
      const name = c.split("=")[0]?.trim();
      if (!name) continue;
      if (name.startsWith("_ga") || name === "_gid" || name === "_gcl_au") {
        document.cookie = `${name}=; Path=/; Max-Age=0; Domain=${eTld}`;
        document.cookie = `${name}=; Path=/; Max-Age=0`;
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Inline script content set as `dangerouslySetInnerHTML` on a `<script>` in
 * the HTML head. Runs before any analytics script has a chance to fire and:
 *
 *   1. Bootstraps window.dataLayer and window.gtag (Google's canonical stub).
 *   2. Sets all Consent Mode v2 categories to `denied` by default.
 *   3. Reads any prior stored decision synchronously and, if analytics was
 *      already granted, immediately updates Consent Mode to `granted` so the
 *      subsequent GA4 script fires with the correct signal.
 *
 * Do not rewrite this to use imports or ES modules — it MUST be inline and
 * synchronous so the default lands before gtag/js is parsed.
 */
export const CONSENT_MODE_BOOTSTRAP = `
(function(){
  try {
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = window.gtag || gtag;
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500
    });
    var stored = null;
    try { stored = localStorage.getItem('${CONSENT_STORAGE_KEY}'); } catch(e) {}
    if (stored) {
      try {
        var parsed = JSON.parse(stored);
        if (parsed && parsed.analytics === 'granted') {
          gtag('consent', 'update', {
            analytics_storage: 'granted',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied'
          });
        }
      } catch(e) {}
    }
  } catch(e) {}
})();
`;
